import React, { useMemo, useState } from 'react';
import {
  Button,
  Divider,
  MenuToggle,
  MenuToggleCheckbox,
  MenuToggleElement,
  Select,
  SelectList,
  SelectOption,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
} from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';
import { User } from '../../types';

interface UserSelectProps {
  availableUsers: User[];
  selectedUsers: string[];
  onSelectionChange: (userIds: string[]) => void;
  placeholder?: string;
  isMultiSelect?: boolean;
  minWidth?: string;
}

export const UserSelect: React.FC<UserSelectProps> = ({
  availableUsers,
  selectedUsers,
  onSelectionChange,
  placeholder = 'Filter users...',
  isMultiSelect = true,
  minWidth = '200px',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Filtered list for search
  const filteredUsers = useMemo(() => {
    if (!searchValue) return availableUsers;
    const searchLower = searchValue.toLowerCase();
    return availableUsers.filter((user) =>
      user.displayName?.toLowerCase().includes(searchLower)
    );
  }, [availableUsers, searchValue]);

  // Checkbox states (for multi-select)
  const allSelected = selectedUsers.length === availableUsers.length && availableUsers.length > 0;
  const noneSelected = selectedUsers.length === 0;
  const checkboxState: boolean | null = noneSelected ? false : allSelected ? true : null;

  const handleCheckboxClick = () => {
    if (checkboxState === true) {
      onSelectionChange([]);
    } else {
      onSelectionChange(availableUsers.map((u) => u.id));
    }
  };

  const handleSelect = (_event: React.MouseEvent | undefined, value: string | number | undefined) => {
    if (value === undefined) return;
    const userId = String(value);

    // Handle select all / deselect all
    if (userId === '__select_all__') {
      onSelectionChange(availableUsers.map((u) => u.id));
      return;
    }
    if (userId === '__deselect_all__') {
      onSelectionChange([]);
      return;
    }

    if (isMultiSelect) {
      const newSelected = selectedUsers.includes(userId)
        ? selectedUsers.filter((id) => id !== userId)
        : [...selectedUsers, userId];
      onSelectionChange(newSelected);
    } else {
      // Single select mode
      onSelectionChange([userId]);
      setSearchValue('');
      setIsOpen(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchValue('');
    }
  };

  // For single-select: show selected user name when not searching, search value when typing
  // For multi-select: always show search value, selected count in placeholder
  const getInputValue = (): string => {
    if (!isMultiSelect && !isOpen && selectedUsers.length === 1) {
      const user = availableUsers.find((u) => u.id === selectedUsers[0]);
      return user?.displayName || '';
    }
    return searchValue;
  };

  const getPlaceholder = (): string => {
    if (!isMultiSelect) {
      return selectedUsers.length === 0 ? placeholder : '';
    }
    return selectedUsers.length > 0 ? `${selectedUsers.length} selected` : placeholder;
  };

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      onClick={() => setIsOpen(!isOpen)}
      isExpanded={isOpen}
      style={{ minWidth, width: minWidth }}
      splitButtonItems={
        isMultiSelect
          ? [
              <MenuToggleCheckbox
                key="users-checkbox"
                id="users-select-checkbox"
                isChecked={checkboxState}
                onChange={handleCheckboxClick}
                aria-label="Select all users"
              />,
            ]
          : undefined
      }
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={getInputValue()}
          onClick={() => setIsOpen(true)}
          onChange={(_event, value) => setSearchValue(value)}
          autoComplete="off"
          placeholder={getPlaceholder()}
          aria-label="Filter users"
        />
        <TextInputGroupUtilities>
          {searchValue && (
            <Button
              variant="plain"
              onClick={() => setSearchValue('')}
              aria-label="Clear filter"
              style={{ padding: '0 4px' }}
            >
              <TimesIcon />
            </Button>
          )}
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  return (
    <Select
      role="menu"
      toggle={toggle}
      onSelect={handleSelect}
      selected={selectedUsers}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <SelectList>
        {isMultiSelect && (
          <>
            <SelectOption
              key="__select_all__"
              value="__select_all__"
              isDisabled={allSelected}
            >
              Select all
            </SelectOption>
            <SelectOption
              key="__deselect_all__"
              value="__deselect_all__"
              isDisabled={noneSelected}
            >
              Deselect all
            </SelectOption>
            <Divider />
          </>
        )}
        {filteredUsers.length === 0 ? (
          <SelectOption isDisabled>No users found</SelectOption>
        ) : (
          filteredUsers.map((user) => (
            <SelectOption
              key={user.id}
              value={user.id}
              hasCheckbox={isMultiSelect}
              isSelected={selectedUsers.includes(user.id)}
            >
              {user.displayName}
            </SelectOption>
          ))
        )}
      </SelectList>
    </Select>
  );
};
